import { useEffect, useState } from 'react';

export type DevicePermission = 'granted' | 'denied' | 'prompt' | 'unknown';

async function query(name: string): Promise<DevicePermission> {
  try {
    const status = await navigator.permissions.query({
      name: name as PermissionName,
    });
    return status.state;
  } catch {
    // Safari has no camera query; an old WebView has no Permissions API.
    return 'unknown';
  }
}

/**
 * Whether the page may use the camera and location, where the browser will
 * say, and a way to ask for either: Settings' Camera and Location rows ask
 * and then show the answer. The wrappers report location themselves, so in
 * one this is the page's view and the rows open the phone's settings.
 */
export function useDevicePermissions() {
  const [camera, setCamera] = useState<DevicePermission>('unknown');
  const [location, setLocation] = useState<DevicePermission>('unknown');
  const read = () => {
    if (!navigator.permissions?.query) return;
    query('camera').then(setCamera);
    query('geolocation').then(setLocation);
  };
  useEffect(read, []);

  const ask = async (kind: 'camera' | 'location') => {
    const allowed = await (kind === 'camera'
      ? askForCamera()
      : askForLocation());
    // A browser with no query still answered the prompt.
    if (!navigator.permissions?.query)
      (kind === 'camera' ? setCamera : setLocation)(
        allowed ? 'granted' : 'denied'
      );
    read();
  };

  return { camera, location, ask };
}

/** Whether this page can ask for the camera at all. */
export const canAskForCamera = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

/**
 * Ask for the camera now, rather than the first time a photo is taken
 * mid-call: the camera is opened and shut straight away, which is what
 * makes the browser, or the wrapper's web view, show its prompt. True when
 * it was allowed. Both wrappers grant getUserMedia on Relay's pages
 * (docs/native-bridge.md); bridge v2 has no message of its own for it.
 */
export async function askForCamera(): Promise<boolean> {
  if (!canAskForCamera()) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    // Refused, or no camera: the person is asked again when they take one.
    return false;
  }
}

/**
 * Ask for the phone's location now, which is what makes the browser show
 * its prompt. True when it was allowed; a refusal or a fix that never comes
 * is false.
 */
export function askForLocation(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.geolocation)
    return Promise.resolve(false);
  return new Promise((resolve) =>
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      (error) => resolve(error.code !== error.PERMISSION_DENIED),
      { timeout: 15_000, maximumAge: 60_000 }
    )
  );
}
